/**
 * no-ruff-supply.js
 *
 * Two-step weight × supply selector for NO RUFF (single-SKU model).
 *
 * The customer always buys the 200 ml variant; the chosen weight × duration
 * tile sets the quantity on the buy-button form. Shopify automatic discounts
 * (qty ≥ N, one per tier) deliver the per-bottle price at checkout.
 *
 * --- SOURCE-OF-TRUTH NOTE ---
 * PRICE_BY_QTY below must mirror the 6 Shopify automatic discounts
 * (titles: "No Ruff · Buy N bottles · X% off"). If those drift, update
 * both. The Jest test theme-jollygut-noruff-selector.test.js asserts the
 * code-side values; live drift is caught by manual QA only.
 *
 * Dose chart (on-pack, once daily, 1 pump = 1.25 ml, 200 ml bottle):
 *   ≤10 kg  → 2 pumps/day → 80-day bottle
 *   10–20 kg → 4 pumps/day → 40-day bottle
 *   20–40 kg → 5 pumps/day → 32-day bottle (chart rounds to 30)
 *   >40 kg  → 6 pumps/day → 27-day bottle (chart rounds to 25)
 */

(function () {
  if (customElements.get("no-ruff-two-step")) return;

  const PUMPS_PER_DAY_BY_WEIGHT = {
    small: 2,
    medium: 4,
    large: 5,
    xlarge: 6,
  };

  const WEIGHT_LABEL = {
    small: "up to 10 kg",
    medium: "10-20 kg",
    large: "20-40 kg",
    xlarge: "over 40 kg",
  };

  // (weight, tile index) → bottles shipped + marketing duration label.
  // Starter is qty=1 for small + medium dogs (₹799 entry), qty=2 for large
  // dogs (₹1,499), qty=3 for giant dogs (₹2,049) — three distinct entry
  // prices that reflect how many bottles each size actually needs.
  // Duration labels round down from real days at chart dose so the customer
  // never runs short. Most Popular and Most Savings ladder up to 120 / 180
  // day labels across the bigger dogs.
  const SUPPLY_BY_WEIGHT_AND_TILE = {
    small: [
      { qty: 1, days: 60 },
      { qty: 2, days: 120 },
      { qty: 3, days: 240 },
    ],
    medium: [
      { qty: 1, days: 40 },
      { qty: 3, days: 120 },
      { qty: 5, days: 180 },
    ],
    large: [
      { qty: 2, days: 60 },
      { qty: 4, days: 120 },
      { qty: 6, days: 180 },
    ],
    xlarge: [
      { qty: 3, days: 75 },
      { qty: 5, days: 120 },
      { qty: 7, days: 180 },
    ],
  };

  // Discounted total price per bottle count (matches the 6 Shopify auto-discounts).
  const PRICE_BY_QTY = {
    1: 799,
    2: 1499,
    3: 2049,
    4: 2499,
    5: 3049,
    6: 3599,
    7: 4199,
  };

  const BASE_PRICE_PER_BOTTLE = 799;
  const MRP_PER_BOTTLE = 849;

  function formatPumps(pumps) {
    return pumps === 1 ? "1 pump" : `${pumps} pumps`;
  }

  function formatMoney(rupees) {
    return "₹" + Math.round(rupees).toLocaleString("en-IN");
  }

  class NoRuffTwoStep extends HTMLElement {
    constructor() {
      super();
      this.selectedWeight = "medium";
      this.selectedTile = 0; // Starter
      this._weightWasClicked = false;
      this._abort = null;
    }

    connectedCallback() {
      this._abort = new AbortController();
      this._formId = this.dataset.noRuffProductFormId;
      this._disableThemeQuantityInputs();
      this._bindWeightButtons();
      this._bindSupplyTiles();
      this._sync({ initial: true });
    }

    _disableThemeQuantityInputs() {
      // The theme's buy-button block may emit its own <input name="quantity">.
      // Disable any that aren't ours so the form only submits the hidden
      // input the selector tiles drive.
      if (!this._formId) return;
      const inputs = document.querySelectorAll(
        `form#${this._formId} input[name="quantity"]:not([data-no-ruff-cart-qty]), input[form="${this._formId}"][name="quantity"]:not([data-no-ruff-cart-qty])`,
      );
      inputs.forEach((input) => {
        input.disabled = true;
      });
    }

    disconnectedCallback() {
      if (this._abort) this._abort.abort();
      this._abort = null;
    }

    _signal() {
      return this._abort ? this._abort.signal : undefined;
    }

    _bindWeightButtons() {
      this.querySelectorAll("[data-no-ruff-weight]").forEach((btn) => {
        btn.addEventListener(
          "click",
          () => {
            this._weightWasClicked = true;
            this.selectedWeight = btn.dataset.noRuffWeight;
            this._sync({ userInitiated: true });
          },
          { signal: this._signal() },
        );
      });
    }

    _bindSupplyTiles() {
      this.querySelectorAll("[data-no-ruff-supply-tile]").forEach((btn) => {
        btn.addEventListener(
          "click",
          () => {
            this.selectedTile = parseInt(btn.dataset.noRuffSupplyTile, 10);
            this._sync({ userInitiated: true });
          },
          { signal: this._signal() },
        );
      });
    }

    _sync() {
      const cell =
        SUPPLY_BY_WEIGHT_AND_TILE[this.selectedWeight][this.selectedTile];

      this.querySelectorAll("[data-no-ruff-weight]").forEach((b) => {
        const active = b.dataset.noRuffWeight === this.selectedWeight;
        b.classList.toggle("is-selected", active);
        b.setAttribute("aria-checked", active ? "true" : "false");
      });

      this.querySelectorAll("[data-no-ruff-supply-tile]").forEach((b) => {
        const idx = parseInt(b.dataset.noRuffSupplyTile, 10);
        const isActive = idx === this.selectedTile;
        b.classList.toggle("is-selected", isActive);
        b.setAttribute("aria-checked", isActive ? "true" : "false");
        const tileCell = SUPPLY_BY_WEIGHT_AND_TILE[this.selectedWeight][idx];
        const daysNode = b.querySelector("[data-no-ruff-supply-days]");
        if (daysNode && tileCell) {
          daysNode.textContent = `${tileCell.days} day pack`;
        }
      });

      const hiddenAttr = this.querySelector("[data-no-ruff-weight-attr]");
      if (hiddenAttr) {
        hiddenAttr.value = this._weightWasClicked
          ? WEIGHT_LABEL[this.selectedWeight] || this.selectedWeight
          : "";
      }

      this._updateFormQuantity(cell.qty);
      this._renderInfoCard(cell);
    }

    _updateFormQuantity(qty) {
      // Primary source of truth: our hidden cart-qty input inside the wrapper.
      const ours = this.querySelector("[data-no-ruff-cart-qty]");
      if (ours && ours.value !== String(qty)) {
        ours.value = qty;
      }
    }

    _renderInfoCard({ qty, days }) {
      const card = this.querySelector("[data-no-ruff-info-card]");
      if (!card) return;

      const finalPrice = PRICE_BY_QTY[qty];
      const mrp = qty * MRP_PER_BOTTLE;
      const save = mrp - finalPrice;
      const pumps = PUMPS_PER_DAY_BY_WEIGHT[this.selectedWeight];

      const set = (sel, text) => {
        const el = card.querySelector(sel);
        if (el) el.textContent = text;
      };

      set(
        '[data-no-ruff-info="supply"]',
        `Includes: ${qty} × 200 ml bottle${qty === 1 ? "" : "s"}`,
      );
      if (finalPrice != null)
        set('[data-no-ruff-info="price"]', formatMoney(finalPrice));
      set('[data-no-ruff-info="compare"]', save > 0 ? formatMoney(mrp) : "");
      set(
        '[data-no-ruff-info="save"]',
        save > 0 ? `Save ${formatMoney(save)}` : "",
      );
      set(
        '[data-no-ruff-info="dose"]',
        `${formatPumps(pumps)} daily · for pets ${WEIGHT_LABEL[this.selectedWeight]}`,
      );
      set('[data-no-ruff-info="duration"]', `${days} days`);

      this._updateThemePrice(finalPrice, mrp, save);
    }

    _updateThemePrice(finalPrice, mrp, save) {
      // Rewrite the theme's main price block so the headline price matches
      // the qty-multiplied total in our info card. Minimog's <m-price> uses
      // .m-price-item--last for the current/sale price and a sibling
      // <s class="m-price-item--regular"> for the strikethrough MRP. We scope
      // to .main-product__block-price (the title-adjacent block) to avoid
      // touching product cards elsewhere on the page.
      const blocks = document.querySelectorAll(
        ".main-product__block-price, .product-block-group--price",
      );
      const finalText = formatMoney(finalPrice);
      const mrpText = formatMoney(mrp);
      blocks.forEach((block) => {
        block.querySelectorAll(".m-price-item--last").forEach((el) => {
          el.textContent = finalText;
        });
        block.querySelectorAll("s.m-price-item--regular").forEach((el) => {
          el.textContent = mrpText;
        });
        // The "Save ₹X" badge — Minimog renders the amount inside .m-currency--saved.
        block.querySelectorAll(".m-currency--saved").forEach((el) => {
          el.textContent = formatMoney(Math.max(0, save));
        });
      });
    }
  }

  customElements.define("no-ruff-two-step", NoRuffTwoStep);
})();
